import { setup, assign, fromPromise } from "xstate";
import { atomWithMachine } from "jotai-xstate";
import type { GetTransferProductsQuery } from "graphql-api/operations";
import { fetchTransferProducts } from "../api";
import {
  ALLOWED_TRANSFER_TYPES,
  TRANSFER_TYPE_SHORT_LABELS,
  DEFAULT_TRANSFER_TYPE_NAME
} from "./transferTypesConfig";

export type TransferType = {
  id: string;
  name: string;
  shortLabel: string;
  image: string | null;
};

const transferTypesMachine = setup({
  types: {} as {
    context: {
      transferTypes: TransferType[];
      loading: boolean;
      error: string | null;
      defaultTransferTypeId: string | null;
    };
    events: { type: "load" } | { type: "retry" };
  },
  actors: {
    fetchTransferTypes: fromPromise<GetTransferProductsQuery, void>(fetchTransferProducts)
  },
  actions: {
    setTransferTypes: assign(({ event }) => {
      const data = (event as any).output as GetTransferProductsQuery;

      if (!data?.transferProducts) {
        return {
          transferTypes: [],
          loading: false,
          error: "No transfer products found",
          defaultTransferTypeId: null
        };
      }

      const filteredProducts = data.transferProducts
        .filter(product => ALLOWED_TRANSFER_TYPES.includes(product.name.toLowerCase()))
        .map(product => {
          const lowerName = product.name.toLowerCase();
          return {
            id: product.id,
            name: product.name,
            shortLabel: TRANSFER_TYPE_SHORT_LABELS[lowerName] || product.name,
            image: product.imageUrl as string | null
          };
        });

      // Sort by the order in ALLOWED_TRANSFER_TYPES
      const sortedProducts = filteredProducts.sort((a, b) => {
        const indexA = ALLOWED_TRANSFER_TYPES.indexOf(a.name.toLowerCase());
        const indexB = ALLOWED_TRANSFER_TYPES.indexOf(b.name.toLowerCase());
        return indexA - indexB;
      });

      // Find the default transfer type ID
      const defaultType = sortedProducts.find(
        t => t.name.toLowerCase() === DEFAULT_TRANSFER_TYPE_NAME
      );

      return {
        transferTypes: sortedProducts,
        loading: false,
        error: null,
        defaultTransferTypeId: defaultType?.id || null
      };
    }),
    setError: assign(({ event }) => ({
      loading: false,
      error: (event as any).error?.message || "Failed to load transfer types"
    })),
    startLoading: assign(() => ({ loading: true, error: null }))
  }
}).createMachine({
  id: "transferTypes",
  context: {
    transferTypes: [],
    loading: false,
    error: null,
    defaultTransferTypeId: null
  },
  initial: "idle",
  states: {
    idle: {
      on: {
        load: "loading"
      },
      entry: [
        // Auto-load on initialization
        ({ self }) => self.send({ type: "load" })
      ]
    },
    loading: {
      entry: "startLoading",
      invoke: {
        src: "fetchTransferTypes",
        onDone: {
          target: "loaded",
          actions: "setTransferTypes"
        },
        onError: {
          target: "error",
          actions: "setError"
        }
      }
    },
    loaded: {
      on: {
        retry: "loading"
      }
    },
    error: {
      on: {
        retry: "loading"
      }
    }
  }
});

export const transferTypesAtom = atomWithMachine(() => transferTypesMachine);
